import { useCallback, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Card, Table } from 'antd'
import { ColumnsType } from 'antd/lib/table/interface'
import isArray from 'lodash/isArray'

import { useAdaptiveLayout, useAppSelector } from 'App'
import { IBusinessAccountsItemDTO } from 'interfaces'
import { showPaginationTotal, sorterConverter } from 'utils'
import { createLoadingSelector, getBusinessAccountsRequest, GET_PERSONAL_ACCOUNTS } from 'store'

import { FullName, CreationDate, Location, BusinessCount, Info } from './components'

const loadingSelector = createLoadingSelector([GET_PERSONAL_ACCOUNTS])

const columns: ColumnsType<IBusinessAccountsItemDTO> = [
  {
    title: 'Company name',
    dataIndex: 'companyName',
    key: 'companyName'
  },
  {
    title: 'Creation date',
    dataIndex: 'createdAt',
    key: 'createdAt',
    sorter: true,
    render: CreationDate
  },
  {
    title: 'Email',
    dataIndex: 'email',
    key: 'email'
  },
  {
    title: 'Location',
    dataIndex: 'location',
    key: 'location',
    render: Location
  },
  {
    title: 'Superadmin name',
    dataIndex: 'superadmin',
    key: 'superadminName',
    render: FullName
  },
  {
    title: 'Admin',
    dataIndex: 'adminCount',
    key: 'adminCount',
    render: BusinessCount
  },
  {
    title: 'Balance, MDT',
    dataIndex: 'balance',
    key: 'balance',
    align: 'right'
  },
  {
    key: 'info',
    align: 'center',
    render: Info
  }
]

export const BusinessAccountsList = () => {
  const dispatch = useDispatch()
  const { isMobile } = useAdaptiveLayout()
  const loading = useAppSelector(loadingSelector)
  const {
    data,
    filters: { current, showBy, total }
  } = useAppSelector((state) => state.userManagement.businessAccounts)

  const showTotal = useCallback(showPaginationTotal, [])

  const handleTableChange: TAnt.TableOnChange<IBusinessAccountsItemDTO> = useCallback(
    ({ current: currentPage, pageSize }, _, sorter, { action }) => {
      const page = currentPage && currentPage > 0 ? currentPage - 1 : 0
      let sorting

      if (!isArray(sorter) && sorter.columnKey && sorter.order) {
        sorting = {
          by: String(sorter.columnKey),
          type: sorterConverter(sorter.order)
        }
      }

      dispatch(
        getBusinessAccountsRequest({
          current: action !== 'paginate' ? 0 : page,
          showBy: pageSize,
          sorting
        })
      )
    },
    [dispatch]
  )

  useEffect(() => {
    dispatch(getBusinessAccountsRequest({ current: 0 }))
  }, [dispatch])

  return (
    <Card bordered={false}>
      <Table
        rowKey={({ createdAt, email }) => createdAt + email}
        columns={columns}
        dataSource={data}
        loading={loading}
        onChange={handleTableChange}
        scroll={{ x: 'max-content' }}
        pagination={{
          responsive: true,
          total,
          defaultPageSize: showBy,
          current: current + 1,
          showTotal: isMobile ? undefined : showTotal
        }}
      />
    </Card>
  )
}
